/* -----------------UI--------------- */
import theme from "@configs/theme";
import type { Stat } from "components/RowStats";

import { getParentMetadata } from "@helpers/content/metadata";

/* -----------------Types--------------- */
import type { Distribution } from "@memorang/types/src/content";

/* -----------------Child components--------------- */
import { useExamContext } from "@features/exam/contexts/ExamContext";

import type { AppConfigMasteryLevelsType } from "@configs/custom-app-config-schema";
import { isScreenshotMode } from "@constants/common";
import { mockedHomePageHeaderStats } from "@constants/mocked-data";
import { hasProductAccess } from "@helpers/content/tag";
import i18n from "@helpers/lib/i18n";
import { randomizeDistribution } from "@helpers/mocked-helpers";
import { useAppStore } from "@hooks/useAppStore";
import { type AppTheme, useAppTheme } from "@hooks/useAppTheme";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useProductMetadata } from "@hooks/useProduct";
import { formatNumber } from "@memorang/helpers/src/common";
import type { TFunction } from "i18next";
import pluralize from "pluralize";
import { useState } from "react";
import { useTranslation } from "react-i18next";
/* -----------------API Calls--------------- */
import { getQuickActionsChips, getWeakTagsIds } from "../helpers/common";
import { getExamDaysLeft, getExamStatus } from "../helpers/stats";
import type { HomeSection } from "../types";
import useFeaturesSection from "./useFeaturesSection";
import useItemsCount from "./useItemsCount";
import { useMasteryKeyTopicsSectionData } from "./useMasteryKeyTopicsSectionData";
import { useRecommendationSection } from "./useRecommendationSection";
import useUserLeaderboard from "./useUserLeaderboard";

type GetHeaderDataProps = {
	distribution: Distribution;
	examIcon?: string;
	examName?: string;
	examDate?: string;
	isDarkMode?: boolean;
	onDateStatItemClick?: () => void;
	t: TFunction;
	accuracy?: number;
	masteryProgressData?: { x: string; y: number }[];
	masteryProgressColorScale?: string[];
	xpEarned?: number;
	examEnabled?: boolean;
};
const getHeaderData = ({
	distribution,
	examName,
	examDate,
	isDarkMode,
	onDateStatItemClick,
	t,
	accuracy,
	examIcon,
	masteryProgressData,
	masteryProgressColorScale,
	xpEarned,
	examEnabled,
}: GetHeaderDataProps) => {
	if (!distribution) {
		return undefined;
	}
	const examStatusText = getExamStatus(examDate);

	const answered =
		Object.values(distribution).reduce((a, b) => a + b, 0) -
		(distribution.none || 0);
	const masteryPercentage = accuracy ? Math.round(accuracy) : 0;

	const daysLeft = getExamDaysLeft(examDate);

	const headerStats: Stat[] = [
		{
			icon: "brain",
			value: t("dashboard.stats.masteryValue", { value: masteryPercentage }),
			label: t("dashboard.stats.mastery"),
			color: theme(isDarkMode).palette.purple.light,
		},
		{
			icon: "check-circle-outline",
			value: t("dashboard.stats.completedValue", { count: answered }),
			label: t("dashboard.stats.completed"),
			color: theme(isDarkMode).palette.success.light,
		},

		...(examEnabled
			? [
					{
						label: examDate
							? t("dashboard.stats.untilExam")
							: t("dashboard.stats.examDate"),
						icon: "calendar-outline",
						color: theme(isDarkMode).colors.primary,
						labelColor: theme(isDarkMode).colors.primary,
						onClick: onDateStatItemClick,
						value: examDate
							? daysLeft && daysLeft > 0
								? t("dashboard.stats.daysLeft", { count: daysLeft })
								: t("dashboard.stats.pastDue")
							: t("dashboard.stats.setExamDate"),
					},
				]
			: [
					{
						icon: "trophy-variant-outline",
						value: t("dashboard.stats.xpEarnedValue", { value: xpEarned }),
						label: t("dashboard.stats.xpEarned"),
						color: theme(isDarkMode).palette.yellow.dark,
					},
				]),
	];
	return {
		distribution: isScreenshotMode
			? randomizeDistribution(distribution)
			: distribution,
		headerStats: isScreenshotMode ? mockedHomePageHeaderStats : headerStats,
		masteryPercentage,
		examIcon,
		examName,
		examStatusText,
		examDate,
		masteryProgressData,
		masteryProgressColorScale,
	};
};

const homeMenus = [
	{
		type: "store" as const,
		label: "Store",
		icon: "cart-outline",
	},
	{
		type: "settings" as const,
		label: "Settings",
		icon: "cog-outline",
	},
];

const getMasteryProgessData = (
	dist: Distribution,
	sortedMasteryLevels: AppConfigMasteryLevelsType,
) => {
	const distMap = Object.keys(dist).reduce<Record<string, number>>(
		(acc, curr) => {
			acc[curr] = dist[curr as keyof Distribution];
			return acc;
		},
		{},
	);

	return [
		...sortedMasteryLevels.map((level) => ({
			x: level.id,
			y: distMap[level.id] || 0,
		})),
		{ x: "none", y: distMap.none || 0 },
	];
};

export const getProgressColorScale = (
	theme: AppTheme,
	sortedMasteryLevels: AppConfigMasteryLevelsType,
): string[] => {
	const { none: empty } = theme.colors.progress;

	return [...sortedMasteryLevels.map((level) => level.lightColor), empty];
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export const useHome = () => {
	const {
		currentExamContext: {
			examIcon,
			studyPackCollectionId,
			examName,
			examDate,
			studyPackProductId,
		},
	} = useExamContext();

	const { t } = useTranslation();

	const { data, isLoading } = useMixedItemDist();

	const theme = useAppTheme();

	const { data: studyPackData } = useMixedItemDist(
		true,
		"GET_STUDYPACK_DISTRIBUTION",
	);
	const {
		isFetchingItemsCount,
		numItemsInStudyPack,
		numTopicsInStudyPack,
		numPracticeTests,
		numQuiz,
	} = useItemsCount(studyPackData);

	const { data: studyPackMetadata } = useProductMetadata(
		"STUDY_PACK",
		studyPackProductId,
	);

	const hasStudyPackProductAccess = hasProductAccess(studyPackMetadata);
	// const hasStudyPackProductAccess = true;

	const isDarkMode = useAppTheme().dark;

	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);
	const showPointsBasedScore = useAppStore(
		(state) => state.reportView?.formative?.showPointsBasedScore,
	);

	const sortedMasteryLevels = useAppStore(
		(state) => state.app.masteryLevels,
	)?.sort((a, b) => a.sortOrder - b.sortOrder);

	const allowChangeExam = useAppStore((state) => state.exam.allowChangeExam);
	const examEnabled = useAppStore((state) => state.exam.enabled);

	const { userLeaderboardResponse } = useUserLeaderboard("all_time");
	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);

	const mixedItemDist = data;

	const hasAccess = isScreenshotMode ? true : hasStudyPackProductAccess;

	const studyPackSubtitle = `Includes ${formatNumber(
		numItemsInStudyPack,
	)} items across ${formatNumber(numTopicsInStudyPack)} topics`;

	const practiceTestSubtitle = `${numPracticeTests ? formatNumber(numPracticeTests) : ""} ${pluralize(practiceTestLabel?.toLocaleLowerCase() ?? "practice test", numPracticeTests)}${numQuiz ? ` • ${formatNumber(numQuiz)} ${pluralize(diagnosticLabel ? diagnosticLabel[i18n.language] : "diagnostic", numQuiz)}` : ""}`;

	const contents = mixedItemDist?.contents || [];

	const { distribution } = getParentMetadata(contents);

	const masteryProgressData = showPointsBasedScore
		? getMasteryProgessData(distribution, sortedMasteryLevels!)
		: undefined;

	const masteryProgressColorScale = showPointsBasedScore
		? getProgressColorScale(theme, sortedMasteryLevels!)
		: undefined;

	const [showDatePicker, setShowDatePicker] = useState(false);

	const weakTagsIds = mixedItemDist ? getWeakTagsIds(mixedItemDist) : [];

	const toggleShowDatePicker = () => {
		setShowDatePicker((prev) => !prev);
	};

	const headerData = mixedItemDist
		? getHeaderData({
				distribution,
				examIcon,
				examName,
				examDate,
				isDarkMode,
				onDateStatItemClick: toggleShowDatePicker,
				t,
				accuracy: mixedItemDist.accuracy,
				masteryProgressData,
				masteryProgressColorScale,
				examEnabled,
				xpEarned: userLeaderboardResponse?.xp,
			})
		: undefined;

	const { featuresSectionData } = useFeaturesSection({
		isFetchingItemsCount,
		practiceTestSubtitle,
		studyPackSubtitle,
		numPracticeTests,
	});

	const masteryKeyTopicsSectionData = useMasteryKeyTopicsSectionData(
		studyPackData,
		hasAccess,
	);

	const quickActionsChips = getQuickActionsChips(studyPackCollectionId);

	const recommendationSectionHookData = useRecommendationSection(
		mixedItemDist ? distribution : undefined,
	);

	const tasksSection = recommendationSectionHookData?.tasksSection;
	const showPurchaseDialog = recommendationSectionHookData?.showPurchaseDialog;
	const toggleShowPurchaseDialog =
		recommendationSectionHookData?.toggleShowPurchaseDialog;

	const showCollectUserDetailsDialog =
		recommendationSectionHookData?.showCollectUserDetailsDialog;
	const toggleShowCollectUserDetailsDialog =
		recommendationSectionHookData?.toggleShowCollectUserDetailsDialog;

	const olxError = recommendationSectionHookData?.olxError;
	const setOlxError = recommendationSectionHookData?.setOlxError;

	const sections: HomeSection[] = [
		...(tasksSection && allowChangeExam ? [tasksSection] : []),
		...(featuresSectionData && allowChangeExam ? [featuresSectionData] : []),
		...(masteryKeyTopicsSectionData ? [masteryKeyTopicsSectionData] : []),
	].map((item) => {
		return {
			title: item.sectionTitle,
			type: item.type,
			loading: item.loading,
			chipProps: "chipProps" in item ? item.chipProps : undefined,
			onPress: item.onPress,
			data: [
				{
					type: item.type,
					items: item.data,
				},
			],
		};
	});

	const exams = studyPackData?.exams;

	return {
		headerData,
		weakTagsIds,
		quickActionsChips,
		sections,
		homeMenus,
		isLoading,
		showPurchaseDialog,
		toggleShowPurchaseDialog,
		olxError,
		setOlxError,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		exams,
		hasAccess,
		showDatePicker,
		toggleShowDatePicker,
	};
};
